import { useMemo, useState } from "react";
import WithAuth from "../../hoc/WithAuth";
import { useContractListForOperation } from "../../api/contract";
import { DataTable } from "../../components/ui/data-table";
import { useNavigate } from "react-router-dom";
import {
  formatDateCreatedValue,
  formatDateToISO,
} from "../../util/date-formatter";
import { getStatusColor } from "../../util/status-formatter";
import { Contract } from "../../types";
import { Button } from "../../components/ui/button";
import { Icons } from "../../assets/images/icons";

const ContractsPage = () => {
  const navigate = useNavigate();

  /** State */
  const [searchText, setSearchText] = useState("");
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(10);

  /** API */
  const { data: contracts, isLoading } = useContractListForOperation({
    page: currentPage - 1,
    size: pageSize,
    search: searchText,
  });

  /** Transform orders for table */
  const transformedContracts = useMemo(() => {
    if (!contracts?.content || !Array.isArray(contracts.content)) return [];

    return contracts.content;
  }, [contracts]);

  /** Columns */
  const contractColumns = [
    {
      key: "index",
      title: "#",
      dataIndex: "index",
      align: "center" as const,
      render: (_: any, __: any, index: number) => index + 1,
    },
    {
      key: "contractNumber",
      title: "Contract Number",
      dataIndex: "contractNumber",
      align: "left" as const,
    },
    {
      key: "fullName",
      title: "Full Name",
      dataIndex: "fullName",
      align: "left" as const,
      render: (text: string) => (
        <div className="truncate max-w-[200px]" title={text}>
          {text}
        </div>
      ),
    },
    {
      key: "phoneNumber",
      title: "Phone Number",
      dataIndex: "phoneNumber",
      align: "left" as const,
    },
    {
      key: "harvestDate",
      title: "Harvest Date",
      dataIndex: "harvestDate",
      align: "left" as const,
      render: (value: string) => formatDateToISO(value),
    },
    {
      key: "dateCreated",
      title: "Date Created",
      dataIndex: "dateCreated",
      align: "left" as const,
      render: (_: unknown, record: Contract) =>
        formatDateCreatedValue(record.dateCreated),
    },
    {
      key: "contractStatus",
      title: "Contract Status",
      dataIndex: "contractStatus",
      align: "left" as const,
      render: (value: string) => (
        <div
          className="px-3 py-1 max-w-[85px] rounded-md text-center text-xs font-medium capitalize"
          style={{
            color: getStatusColor(value, "text"),
            backgroundColor: getStatusColor(value, "bg"),
          }}
        >
          {value?.charAt(0).toUpperCase() + value?.slice(1).toLowerCase()}
        </div>
      ),
    },
    {
      key: "actions",
      title: "Actions",
      align: "center" as const,
      render: (_: any, record: Contract) => (
        <Button
          variant="destructive"
          size="sm"
          onClick={(e) => {
            e.stopPropagation();
          }}
          className="h-8 p-0 w-full gap-2 text-[#C41919]"
        >
          <img src={Icons.deleteIcon} alt="delete" className="h-4 w-4" />
          Remove
        </Button>
      ),
    },
  ];

  /** Handlers */
  const handlePaginationChange = (page: number, size: number) => {
    setCurrentPage(page);
    setPageSize(size);
  };

  const handleRowClick = (contract: any) => {
    navigate(`/contracts/${contract.id}`);
  };

  const handleSearch = (value: string) => {
    setSearchText(value);
    setCurrentPage(1);
  };

  return (
    <DataTable<Contract>
      data={transformedContracts}
      columns={contractColumns}
      loading={isLoading}
      pagination={{
        current: currentPage,
        pageSize: pageSize,
        total: contracts?.totalElements ?? 0,
        onChange: handlePaginationChange,
      }}
      onRowClick={handleRowClick}
      search={{
        placeholder: "Search contracts by name",
        value: searchText,
        onChange: handleSearch,
      }}
      emptyIcon={<img src={Icons.noDataIcon} alt="No data" />}
      emptyText="No contracts found"
      rowKey="id"
    />
  );
};

export default WithAuth(ContractsPage);
